import React, { Component, type ErrorInfo, type ReactNode } from 'react';
import { Button } from './';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    console.error('ErrorBoundary caught an error:', error, errorInfo);
    this.props.onError?.(error, errorInfo);
  }

  handleRetry = (): void => {
    this.setState({ hasError: false, error: null });
  };

  render(): ReactNode {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <div className='flex flex-col items-center justify-center min-h-[400px] p-8'>
          <div className='text-center max-w-md'>
            <h2 className='text-2xl font-bold text-gray-800 mb-4'>
              Something went wrong
            </h2>
            <p className='text-gray-600 mb-6'>
              We encountered an unexpected error. Please try again or contact support if the problem persists.
            </p>
            <div className='flex gap-4 justify-center'>
              <Button
                color='primary'
                onClick={this.handleRetry}
              >
                Try Again
              </Button>
              <Button
                color='dark'
                onClick={() => window.location.reload()}
              >
                Refresh Page
              </Button>
            </div>
            {process.env.NODE_ENV === 'development' && this.state.error && (
              <details className='mt-6 text-left'>
                <summary className='cursor-pointer text-sm text-gray-500'>
                  Error Details (Development Only)
                </summary>
                <pre className='mt-2 p-4 bg-gray-100 rounded-lg text-xs text-red-600 overflow-auto'>
                  {this.state.error.toString()}
                  {'\n'}
                  {this.state.error.stack}
                </pre>
              </details>
            )}
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
